<template>
  <div :class="loaderClasses" :style="colorStyle" role="status" aria-busy="true">
    <!-- Hidden SVG filter for liquid effect -->
    <svg v-if="type === 'liquid'" class="ava-loader__filter-defs" aria-hidden="true">
      <defs>
        <filter id="ava-loader-liquid-filter">
          <feGaussianBlur in="SourceGraphic" stdDeviation="8" result="blur" />
          <feColorMatrix in="blur" mode="matrix" values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 20 -10" result="contrast" />
          <feComposite in="SourceGraphic" in2="contrast" operator="atop" />
        </filter>
      </defs>
    </svg>

    <!-- Overlay backdrop -->
    <div v-if="overlay" class="ava-loader__backdrop"></div>

    <!-- Loader container -->
    <div class="ava-loader__container">
      <!-- Ripple rings (ripple type only) -->
      <template v-if="type === 'ripple'">
        <div class="ava-loader__ripple-ring"></div>
        <div class="ava-loader__ripple-ring"></div>
        <div class="ava-loader__ripple-ring"></div>
      </template>

      <!-- Particles (burst type only) -->
      <template v-if="type === 'burst'">
        <div v-for="i in 8" :key="'p' + i" :class="'ava-loader__particle ava-loader__particle--' + i"></div>
      </template>

      <!-- Signal waves (signal type only) -->
      <div v-if="type === 'signal'" class="ava-loader__signal-wave">
        <div v-for="i in 5" :key="'w' + i" :class="'ava-loader__wave-line ava-loader__wave-line--' + i"></div>
      </div>

      <!-- Hologram scan line (hologram type only) -->
      <div v-if="type === 'hologram'" class="ava-loader__scan-line"></div>

      <!-- Main SVG -->
      <svg class="ava-loader__svg" viewBox="0 0 230 100" :aria-label="text || 'Loading'">
        <!-- Base shapes -->
        <path class="ava-loader__shape-right" d="M220.17,11.82v50.04c0,2.69-.9,5.18-2.42,7.16l-27.06,27.06s-.02.03-.03.04l-.8.8s-.03.02-.04.03c-2.1,1.89-4.87,3.04-7.91,3.04h-50.08c-3.26,0-6.22-1.32-8.36-3.47-2.14-2.14-3.46-5.09-3.46-8.36v-50.04c0-3.26,1.32-6.22,3.46-8.36l26.31-26.18.13-.14s.09-.09.14-.13C152.16,1.27,155.06,0,158.26,0h50.09c3.27,0,6.22,1.32,8.36,3.46,2.14,2.14,3.46,5.09,3.46,8.36Z"/>
        <path class="ava-loader__shape-left" d="M88.35,10c.63,0,1.05.29,1.29.53.24.24.53.66.53,1.29v50.04c0,.28-.06.54-.17.77l-26.36,26.36c-.07.07-.15.15-.22.22l-.39.39c-.32.26-.7.39-1.13.39H11.82c-.62,0-1.05-.29-1.29-.54-.24-.24-.53-.66-.53-1.29v-50.04c0-.63.3-1.05.52-1.27l26.31-26.18.1-.1.06-.07.02-.02c.24-.23.65-.51,1.26-.51h50.09M88.35,0h-50.09C35.06,0,32.16,1.27,30.04,3.33c-.04.04-.09.09-.14.13l-.13.14L3.46,29.78c-2.14,2.14-3.46,5.1-3.46,8.36v50.04c0,3.26,1.33,6.22,3.46,8.36,2.14,2.14,5.1,3.47,8.36,3.47h50.08c3.04,0,5.82-1.15,7.91-3.04.01-.01.02-.02.04-.03l.8-.8s.02-.03.03-.04l27.06-27.06c1.52-1.98,2.42-4.47,2.42-7.16V11.82c0-3.27-1.33-6.22-3.46-8.36C94.57,1.32,91.62,0,88.35,0h0Z"/>

        <!-- Wave fill paths (wave type only) -->
        <template v-if="type === 'wave'">
          <path class="ava-loader__shape-right-fill" d="M220.17,11.82v50.04c0,2.69-.9,5.18-2.42,7.16l-27.06,27.06s-.02.03-.03.04l-.8.8s-.03.02-.04.03c-2.1,1.89-4.87,3.04-7.91,3.04h-50.08c-3.26,0-6.22-1.32-8.36-3.47-2.14-2.14-3.46-5.09-3.46-8.36v-50.04c0-3.26,1.32-6.22,3.46-8.36l26.31-26.18.13-.14s.09-.09.14-.13C152.16,1.27,155.06,0,158.26,0h50.09c3.27,0,6.22,1.32,8.36,3.46,2.14,2.14,3.46,5.09,3.46,8.36Z"/>
          <path class="ava-loader__shape-left-fill" d="M88.35,10c.63,0,1.05.29,1.29.53.24.24.53.66.53,1.29v50.04c0,.28-.06.54-.17.77l-26.36,26.36c-.07.07-.15.15-.22.22l-.39.39c-.32.26-.7.39-1.13.39H11.82c-.62,0-1.05-.29-1.29-.54-.24-.24-.53-.66-.53-1.29v-50.04c0-.63.3-1.05.52-1.27l26.31-26.18.1-.1.06-.07.02-.02c.24-.23.65-.51,1.26-.51h50.09M88.35,0h-50.09C35.06,0,32.16,1.27,30.04,3.33c-.04.04-.09.09-.14.13l-.13.14L3.46,29.78c-2.14,2.14-3.46,5.1-3.46,8.36v50.04c0,3.26,1.33,6.22,3.46,8.36,2.14,2.14,5.1,3.47,8.36,3.47h50.08c3.04,0,5.82-1.15,7.91-3.04.01-.01.02-.02.04-.03l.8-.8s.02-.03.03-.04l27.06-27.06c1.52-1.98,2.42-4.47,2.42-7.16V11.82c0-3.27-1.33-6.22-3.46-8.36C94.57,1.32,91.62,0,88.35,0h0Z"/>
        </template>

        <!-- Energy fill paths (energy type only) -->
        <template v-if="type === 'energy'">
          <path class="ava-loader__energy-fill-right" d="M220.17,11.82v50.04c0,2.69-.9,5.18-2.42,7.16l-27.06,27.06s-.02.03-.03.04l-.8.8s-.03.02-.04.03c-2.1,1.89-4.87,3.04-7.91,3.04h-50.08c-3.26,0-6.22-1.32-8.36-3.47-2.14-2.14-3.46-5.09-3.46-8.36v-50.04c0-3.26,1.32-6.22,3.46-8.36l26.31-26.18.13-.14s.09-.09.14-.13C152.16,1.27,155.06,0,158.26,0h50.09c3.27,0,6.22,1.32,8.36,3.46,2.14,2.14,3.46,5.09,3.46,8.36Z"/>
          <path class="ava-loader__energy-fill-left" d="M88.35,10c.63,0,1.05.29,1.29.53.24.24.53.66.53,1.29v50.04c0,.28-.06.54-.17.77l-26.36,26.36c-.07.07-.15.15-.22.22l-.39.39c-.32.26-.7.39-1.13.39H11.82c-.62,0-1.05-.29-1.29-.54-.24-.24-.53-.66-.53-1.29v-50.04c0-.63.3-1.05.52-1.27l26.31-26.18.1-.1.06-.07.02-.02c.24-.23.65-.51,1.26-.51h50.09M88.35,0h-50.09C35.06,0,32.16,1.27,30.04,3.33c-.04.04-.09.09-.14.13l-.13.14L3.46,29.78c-2.14,2.14-3.46,5.1-3.46,8.36v50.04c0,3.26,1.33,6.22,3.46,8.36,2.14,2.14,5.1,3.47,8.36,3.47h50.08c3.04,0,5.82-1.15,7.91-3.04.01-.01.02-.02.04-.03l.8-.8s.02-.03.03-.04l27.06-27.06c1.52-1.98,2.42-4.47,2.42-7.16V11.82c0-3.27-1.33-6.22-3.46-8.36C94.57,1.32,91.62,0,88.35,0h0Z"/>
        </template>
      </svg>

      <!-- Glitch layers (glitch type only) -->
      <template v-if="type === 'glitch'">
        <div class="ava-loader__glitch-layer ava-loader__glitch-red">
          <svg viewBox="0 0 230 100">
            <path class="ava-loader__shape-right" d="M220.17,11.82v50.04c0,2.69-.9,5.18-2.42,7.16l-27.06,27.06s-.02.03-.03.04l-.8.8s-.03.02-.04.03c-2.1,1.89-4.87,3.04-7.91,3.04h-50.08c-3.26,0-6.22-1.32-8.36-3.47-2.14-2.14-3.46-5.09-3.46-8.36v-50.04c0-3.26,1.32-6.22,3.46-8.36l26.31-26.18.13-.14s.09-.09.14-.13C152.16,1.27,155.06,0,158.26,0h50.09c3.27,0,6.22,1.32,8.36,3.46,2.14,2.14,3.46,5.09,3.46,8.36Z"/>
            <path class="ava-loader__shape-left" d="M88.35,10c.63,0,1.05.29,1.29.53.24.24.53.66.53,1.29v50.04c0,.28-.06.54-.17.77l-26.36,26.36c-.07.07-.15.15-.22.22l-.39.39c-.32.26-.7.39-1.13.39H11.82c-.62,0-1.05-.29-1.29-.54-.24-.24-.53-.66-.53-1.29v-50.04c0-.63.3-1.05.52-1.27l26.31-26.18.1-.1.06-.07.02-.02c.24-.23.65-.51,1.26-.51h50.09M88.35,0h-50.09C35.06,0,32.16,1.27,30.04,3.33c-.04.04-.09.09-.14.13l-.13.14L3.46,29.78c-2.14,2.14-3.46,5.1-3.46,8.36v50.04c0,3.26,1.33,6.22,3.46,8.36,2.14,2.14,5.1,3.47,8.36,3.47h50.08c3.04,0,5.82-1.15,7.91-3.04.01-.01.02-.02.04-.03l.8-.8s.02-.03.03-.04l27.06-27.06c1.52-1.98,2.42-4.47,2.42-7.16V11.82c0-3.27-1.33-6.22-3.46-8.36C94.57,1.32,91.62,0,88.35,0h0Z"/>
          </svg>
        </div>
        <div class="ava-loader__glitch-layer ava-loader__glitch-blue">
          <svg viewBox="0 0 230 100">
            <path class="ava-loader__shape-right" d="M220.17,11.82v50.04c0,2.69-.9,5.18-2.42,7.16l-27.06,27.06s-.02.03-.03.04l-.8.8s-.03.02-.04.03c-2.1,1.89-4.87,3.04-7.91,3.04h-50.08c-3.26,0-6.22-1.32-8.36-3.47-2.14-2.14-3.46-5.09-3.46-8.36v-50.04c0-3.26,1.32-6.22,3.46-8.36l26.31-26.18.13-.14s.09-.09.14-.13C152.16,1.27,155.06,0,158.26,0h50.09c3.27,0,6.22,1.32,8.36,3.46,2.14,2.14,3.46,5.09,3.46,8.36Z"/>
            <path class="ava-loader__shape-left" d="M88.35,10c.63,0,1.05.29,1.29.53.24.24.53.66.53,1.29v50.04c0,.28-.06.54-.17.77l-26.36,26.36c-.07.07-.15.15-.22.22l-.39.39c-.32.26-.7.39-1.13.39H11.82c-.62,0-1.05-.29-1.29-.54-.24-.24-.53-.66-.53-1.29v-50.04c0-.63.3-1.05.52-1.27l26.31-26.18.1-.1.06-.07.02-.02c.24-.23.65-.51,1.26-.51h50.09M88.35,0h-50.09C35.06,0,32.16,1.27,30.04,3.33c-.04.04-.09.09-.14.13l-.13.14L3.46,29.78c-2.14,2.14-3.46,5.1-3.46,8.36v50.04c0,3.26,1.33,6.22,3.46,8.36,2.14,2.14,5.1,3.47,8.36,3.47h50.08c3.04,0,5.82-1.15,7.91-3.04.01-.01.02-.02.04-.03l.8-.8s.02-.03.03-.04l27.06-27.06c1.52-1.98,2.42-4.47,2.42-7.16V11.82c0-3.27-1.33-6.22-3.46-8.36C94.57,1.32,91.62,0,88.35,0h0Z"/>
          </svg>
        </div>
      </template>
    </div>

    <!-- Loading text -->
    <span v-if="text" class="ava-loader__text">{{ text }}</span>
  </div>
</template>

<script>
/**
 * AvaLoader - Generic view loader component with multiple animation types
 *
 * @example
 * // Default pulse animation
 * <AvaLoader />
 *
 * // With specific animation type
 * <AvaLoader type="hologram" />
 *
 * // As overlay with loading text
 * <AvaLoader overlay text="Loading data..." />
 */
export default {
  name: 'AvaLoader',

  props: {
    /**
     * Animation type
     * @values pulse, wave, stroke, glitch, liquid, ripple, pendulum, burst, signal, hologram, magnetic, energy, typewriter, spiral
     */
    type: {
      type: String,
      default: 'pulse',
      validator: (value) =>
        [
          'pulse',
          'wave',
          'stroke',
          'glitch',
          'liquid',
          'ripple',
          'pendulum',
          'burst',
          'signal',
          'hologram',
          'magnetic',
          'energy',
          'typewriter',
          'spiral',
        ].includes(value),
    },
    /**
     * Size variant
     * @values small, medium, large
     */
    size: {
      type: String,
      default: 'medium',
      validator: (value) => ['small', 'medium', 'large'].includes(value),
    },
    /**
     * Optional loading text displayed below the loader
     */
    text: {
      type: String,
      default: '',
    },
    /**
     * Show as overlay covering parent container
     */
    overlay: {
      type: Boolean,
      default: false,
    },
    /**
     * Custom color override (CSS color value)
     */
    color: {
      type: String,
      default: '',
    },
  },

  computed: {
    loaderClasses() {
      return [
        'ava-loader',
        `ava-loader--${this.type}`,
        `ava-loader--${this.size}`,
        { 'ava-loader--overlay': this.overlay },
      ];
    },
    colorStyle() {
      return this.color ? { '--loader-color': this.color } : {};
    },
  },
};
</script>

<style lang="scss" scoped>
// ============================================
// AvaLoader Component Styles
// ============================================

.ava-loader {
  // Base color variable - allows override via color prop
  --loader-color: var(--v-primary-base, #03624c);

  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;

  // Hidden filter defs
  &__filter-defs {
    position: absolute;
    width: 0;
    height: 0;
    overflow: hidden;
  }

  // ==========================================
  // Size Variants
  // ==========================================
  &--small &__container {
    width: 90px;
    height: 40px;
  }

  &--medium &__container {
    width: 140px;
    height: 60px;
  }

  &--large &__container {
    width: 180px;
    height: 78px;
  }

  // ==========================================
  // Overlay Mode
  // ==========================================
  &--overlay {
    position: absolute;
    inset: 0;
    z-index: 10;
  }

  &__backdrop {
    position: absolute;
    inset: 0;
    background: var(--v-bg2-base, rgba(0, 0, 0, 0.85));
    opacity: 0.95;
    backdrop-filter: blur(2px);
  }

  // ==========================================
  // Container & SVG Base
  // ==========================================
  &__container {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__svg {
    width: 100%;
    height: 100%;
  }

  &__shape-left,
  &__shape-right {
    fill: var(--loader-color);
  }

  // ==========================================
  // Loading Text
  // ==========================================
  &__text {
    margin-top: 16px;
    font-size: 0.875rem;
    color: var(--v-text_secondary-base, #8899aa);
    text-align: center;
    z-index: 1;
  }

  // ==========================================
  // ANIMATION: Pulse (Default)
  // ==========================================
  &--pulse &__shape-right {
    animation: vl-pulse-right 1.4s ease-in-out infinite;
    transform-origin: 165px 50px;
  }

  &--pulse &__shape-left {
    animation: vl-pulse-left 1.4s ease-in-out infinite;
    transform-origin: 50px 50px;
  }

  // ==========================================
  // ANIMATION: Wave Fill
  // ==========================================
  &--wave &__shape-left,
  &--wave &__shape-right {
    fill: rgba(var(--v-primary-base), 0.2);
    fill: color-mix(in srgb, var(--loader-color) 20%, transparent);
  }

  &--wave &__shape-left-fill,
  &--wave &__shape-right-fill {
    fill: var(--loader-color);
    clip-path: inset(100% 0 0 0);
  }

  &--wave &__shape-left-fill {
    animation: vl-wave-fill-left 2s ease-in-out infinite;
  }

  &--wave &__shape-right-fill {
    animation: vl-wave-fill-right 2s ease-in-out infinite;
  }

  // ==========================================
  // ANIMATION: Stroke Draw
  // ==========================================
  &--stroke &__shape-left,
  &--stroke &__shape-right {
    fill: none;
    stroke: var(--loader-color);
    stroke-width: 2;
    stroke-dasharray: 400;
    stroke-dashoffset: 400;
    stroke-linecap: round;
  }

  &--stroke &__shape-left {
    animation: vl-stroke-draw 2.5s ease-in-out infinite;
  }

  &--stroke &__shape-right {
    animation: vl-stroke-draw 2.5s ease-in-out infinite 0.3s;
  }

  // ==========================================
  // ANIMATION: Glitch Effect
  // ==========================================
  &--glitch {
    position: relative;
  }

  &--glitch &__svg {
    animation: vl-glitch-main 3s ease-in-out infinite;
  }

  &__glitch-layer {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;

    svg {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
    }
  }

  &__glitch-red {
    animation: vl-glitch-red 3s ease-in-out infinite;
    opacity: 0.6;
    mix-blend-mode: screen;

    .ava-loader__shape-left,
    .ava-loader__shape-right {
      fill: #ff3366;
    }
  }

  &__glitch-blue {
    animation: vl-glitch-blue 3s ease-in-out infinite;
    opacity: 0.6;
    mix-blend-mode: screen;

    .ava-loader__shape-left,
    .ava-loader__shape-right {
      fill: #33ccff;
    }
  }

  // ==========================================
  // ANIMATION: Liquid Morph
  // ==========================================
  &--liquid &__svg {
    filter: url(#ava-loader-liquid-filter);
  }

  &--liquid &__shape-left {
    animation: vl-liquid-left 2s ease-in-out infinite;
    transform-origin: center;
  }

  &--liquid &__shape-right {
    animation: vl-liquid-right 2s ease-in-out infinite;
    transform-origin: center;
  }

  // ==========================================
  // ANIMATION: Ripple Expand
  // ==========================================
  &__ripple-ring {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 40px;
    height: 40px;
    border: 2px solid var(--loader-color);
    border-radius: 50%;
    transform: translate(-50%, -50%) scale(0);
    opacity: 0;
    animation: vl-ripple-expand 2s ease-out infinite;

    &:nth-child(2) {
      animation-delay: 0.4s;
    }
    &:nth-child(3) {
      animation-delay: 0.8s;
    }
  }

  &--ripple &__svg {
    position: relative;
    z-index: 2;
  }

  // ==========================================
  // ANIMATION: Pendulum Swing
  // ==========================================
  &--pendulum &__shape-left {
    animation: vl-pendulum-left 1.6s cubic-bezier(0.4, 0, 0.2, 1) infinite;
    transform-origin: 90px 0;
  }

  &--pendulum &__shape-right {
    animation: vl-pendulum-right 1.6s cubic-bezier(0.4, 0, 0.2, 1) infinite;
    transform-origin: 130px 0;
  }

  // ==========================================
  // ANIMATION: Particle Burst
  // ==========================================
  &__particle {
    position: absolute;
    width: 4px;
    height: 4px;
    background: var(--loader-color);
    border-radius: 50%;
    opacity: 0;
    top: 50%;
    left: 50%;
  }

  &__particle--1 {
    animation: vl-burst-particle 2s ease-out infinite;
    --angle: 0deg;
    --distance: 50px;
  }
  &__particle--2 {
    animation: vl-burst-particle 2s ease-out infinite 0.1s;
    --angle: 45deg;
    --distance: 45px;
  }
  &__particle--3 {
    animation: vl-burst-particle 2s ease-out infinite 0.2s;
    --angle: 90deg;
    --distance: 55px;
  }
  &__particle--4 {
    animation: vl-burst-particle 2s ease-out infinite 0.05s;
    --angle: 135deg;
    --distance: 48px;
  }
  &__particle--5 {
    animation: vl-burst-particle 2s ease-out infinite 0.15s;
    --angle: 180deg;
    --distance: 52px;
  }
  &__particle--6 {
    animation: vl-burst-particle 2s ease-out infinite 0.25s;
    --angle: 225deg;
    --distance: 46px;
  }
  &__particle--7 {
    animation: vl-burst-particle 2s ease-out infinite 0.08s;
    --angle: 270deg;
    --distance: 54px;
  }
  &__particle--8 {
    animation: vl-burst-particle 2s ease-out infinite 0.18s;
    --angle: 315deg;
    --distance: 49px;
  }

  &--burst &__svg {
    animation: vl-burst-pulse 2s ease-in-out infinite;
  }

  // ==========================================
  // ANIMATION: Signal Broadcast
  // ==========================================
  &__signal-wave {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 100%;
    height: 60px;
    transform: translate(-50%, -50%);
    pointer-events: none;
  }

  &__wave-line {
    position: absolute;
    top: 50%;
    width: 100%;
    height: 2px;
    background: linear-gradient(90deg, transparent, var(--loader-color), var(--loader-color), transparent);
    transform: translateY(-50%) scaleY(0);
    animation: vl-signal-wave 1.5s ease-in-out infinite;

    &--1 {
      animation-delay: 0s;
    }
    &--2 {
      animation-delay: 0.15s;
      top: 35%;
    }
    &--3 {
      animation-delay: 0.15s;
      top: 65%;
    }
    &--4 {
      animation-delay: 0.3s;
      top: 20%;
    }
    &--5 {
      animation-delay: 0.3s;
      top: 80%;
    }
  }

  &--signal &__svg {
    position: relative;
    z-index: 2;
  }

  // ==========================================
  // ANIMATION: Hologram Scan
  // ==========================================
  &--hologram {
    overflow: hidden;
  }

  &--hologram &__shape-left,
  &--hologram &__shape-right {
    fill: transparent;
    stroke: var(--loader-color);
    stroke-width: 1.5;
  }

  &__scan-line {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 3px;
    background: linear-gradient(180deg, transparent, var(--loader-color), color-mix(in srgb, var(--loader-color) 50%, transparent), transparent);
    animation: vl-hologram-scan 2s linear infinite;
    box-shadow: 0 0 15px var(--loader-color), 0 0 30px color-mix(in srgb, var(--loader-color) 50%, transparent);
  }

  &--hologram &__svg {
    filter: drop-shadow(0 0 3px color-mix(in srgb, var(--loader-color) 50%, transparent));
    animation: vl-hologram-flicker 0.15s ease-in-out infinite alternate;
  }

  // ==========================================
  // ANIMATION: Magnetic Pull
  // ==========================================
  &--magnetic &__shape-left {
    animation: vl-magnetic-left 1.8s ease-in-out infinite;
    transform-origin: 50px 50px;
  }

  &--magnetic &__shape-right {
    animation: vl-magnetic-right 1.8s ease-in-out infinite;
    transform-origin: 165px 50px;
  }

  // ==========================================
  // ANIMATION: Energy Charge
  // ==========================================
  &--energy &__shape-left,
  &--energy &__shape-right {
    fill: color-mix(in srgb, var(--loader-color) 20%, transparent);
    stroke: var(--loader-color);
    stroke-width: 1;
  }

  &__energy-fill-left,
  &__energy-fill-right {
    fill: var(--loader-color);
    clip-path: polygon(0 100%, 100% 100%, 100% 100%, 0 100%);
  }

  &__energy-fill-left {
    animation: vl-energy-charge-left 2.5s ease-out infinite;
  }

  &__energy-fill-right {
    animation: vl-energy-charge-right 2.5s ease-out infinite 1.25s;
  }

  &--energy &__svg {
    filter: drop-shadow(0 0 8px color-mix(in srgb, var(--loader-color) 40%, transparent));
  }

  // ==========================================
  // ANIMATION: Typewriter Reveal
  // ==========================================
  &--typewriter {
    position: relative;
  }

  &--typewriter &__svg {
    clip-path: inset(0 100% 0 0);
    animation: vl-typewriter-reveal 2s steps(20) infinite;
  }

  &--typewriter::after {
    content: '';
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 3px;
    height: 60%;
    background: var(--loader-color);
    animation: vl-typewriter-cursor 0.6s ease-in-out infinite;
  }

  // ==========================================
  // ANIMATION: Spiral Rotation
  // ==========================================
  &--spiral &__svg {
    animation: vl-spiral-rotate 3s linear infinite;
    transform-origin: center;
  }

  &--spiral &__shape-left {
    animation: vl-spiral-scale 1.5s ease-in-out infinite;
    transform-origin: 50px 50px;
  }

  &--spiral &__shape-right {
    animation: vl-spiral-scale 1.5s ease-in-out infinite 0.75s;
    transform-origin: 165px 50px;
  }

  // ==========================================
  // Reduced motion preference
  // ==========================================
  @media (prefers-reduced-motion: reduce) {
    &__shape-left,
    &__shape-right,
    &__svg,
    &__scan-line,
    &__ripple-ring,
    &__particle,
    &__wave-line,
    &__glitch-layer {
      animation: none !important;
    }

    &--pulse &__shape-left,
    &--pulse &__shape-right {
      opacity: 0.7;
    }
  }
}

// ==========================================
// KEYFRAMES
// ==========================================

// Pulse
@keyframes vl-pulse-right {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.3;
    transform: scale(0.88);
  }
}

@keyframes vl-pulse-left {
  0%,
  100% {
    opacity: 0.3;
    transform: scale(0.88);
  }
  50% {
    opacity: 1;
    transform: scale(1);
  }
}

// Wave Fill
@keyframes vl-wave-fill-left {
  0%,
  100% {
    clip-path: inset(100% 0 0 0);
  }
  25%,
  75% {
    clip-path: inset(0 0 0 0);
  }
}

@keyframes vl-wave-fill-right {
  0%,
  50% {
    clip-path: inset(100% 0 0 0);
  }
  75%,
  100% {
    clip-path: inset(0 0 0 0);
  }
  100% {
    clip-path: inset(100% 0 0 0);
  }
}

// Stroke Draw
@keyframes vl-stroke-draw {
  0% {
    stroke-dashoffset: 400;
    fill: transparent;
  }
  40% {
    stroke-dashoffset: 0;
    fill: transparent;
  }
  60% {
    stroke-dashoffset: 0;
    fill: color-mix(in srgb, var(--loader-color, #03624c) 30%, transparent);
  }
  80% {
    stroke-dashoffset: 0;
    fill: color-mix(in srgb, var(--loader-color, #03624c) 30%, transparent);
  }
  100% {
    stroke-dashoffset: -400;
    fill: transparent;
  }
}

// Glitch
@keyframes vl-glitch-main {
  0%,
  90%,
  100% {
    transform: translate(0);
  }
  92% {
    transform: translate(-2px, 1px);
  }
  94% {
    transform: translate(2px, -1px);
  }
  96% {
    transform: translate(-1px, 2px);
  }
  98% {
    transform: translate(1px, -2px);
  }
}

@keyframes vl-glitch-red {
  0%,
  90%,
  100% {
    transform: translate(0);
    opacity: 0;
  }
  91% {
    opacity: 0.6;
    transform: translate(-3px, 0);
  }
  93% {
    opacity: 0.6;
    transform: translate(3px, 0);
  }
  95% {
    opacity: 0.6;
    transform: translate(-2px, 1px);
  }
  97% {
    opacity: 0;
  }
}

@keyframes vl-glitch-blue {
  0%,
  90%,
  100% {
    transform: translate(0);
    opacity: 0;
  }
  92% {
    opacity: 0.6;
    transform: translate(3px, 0);
  }
  94% {
    opacity: 0.6;
    transform: translate(-3px, 0);
  }
  96% {
    opacity: 0.6;
    transform: translate(2px, -1px);
  }
  98% {
    opacity: 0;
  }
}

// Liquid Morph
@keyframes vl-liquid-left {
  0%,
  100% {
    transform: translateX(0) scaleX(1);
  }
  50% {
    transform: translateX(15px) scaleX(1.1);
  }
}

@keyframes vl-liquid-right {
  0%,
  100% {
    transform: translateX(0) scaleX(1);
  }
  50% {
    transform: translateX(-15px) scaleX(1.1);
  }
}

// Ripple Expand
@keyframes vl-ripple-expand {
  0% {
    transform: translate(-50%, -50%) scale(0);
    opacity: 0.8;
  }
  100% {
    transform: translate(-50%, -50%) scale(4);
    opacity: 0;
  }
}

// Pendulum Swing
@keyframes vl-pendulum-left {
  0%,
  100% {
    transform: rotate(-8deg);
  }
  50% {
    transform: rotate(8deg);
  }
}

@keyframes vl-pendulum-right {
  0%,
  100% {
    transform: rotate(8deg);
  }
  50% {
    transform: rotate(-8deg);
  }
}

// Particle Burst
@keyframes vl-burst-pulse {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(0.95);
  }
}

@keyframes vl-burst-particle {
  0% {
    opacity: 0;
    transform: translate(-50%, -50%) rotate(var(--angle)) translateY(0);
  }
  10% {
    opacity: 1;
  }
  100% {
    opacity: 0;
    transform: translate(-50%, -50%) rotate(var(--angle)) translateY(calc(var(--distance) * -1));
  }
}

// Signal Broadcast
@keyframes vl-signal-wave {
  0%,
  100% {
    transform: translateY(-50%) scaleY(0);
    opacity: 0;
  }
  50% {
    transform: translateY(-50%) scaleY(1);
    opacity: 1;
  }
}

// Hologram Scan
@keyframes vl-hologram-scan {
  0% {
    top: -10%;
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  90% {
    opacity: 1;
  }
  100% {
    top: 110%;
    opacity: 0;
  }
}

@keyframes vl-hologram-flicker {
  0% {
    opacity: 0.9;
  }
  100% {
    opacity: 1;
  }
}

// Magnetic Pull
@keyframes vl-magnetic-left {
  0%,
  100% {
    transform: translateX(0) rotate(0deg);
  }
  25% {
    transform: translateX(20px) rotate(3deg);
  }
  50% {
    transform: translateX(0) rotate(0deg);
  }
  75% {
    transform: translateX(-10px) rotate(-2deg);
  }
}

@keyframes vl-magnetic-right {
  0%,
  100% {
    transform: translateX(0) rotate(0deg);
  }
  25% {
    transform: translateX(-20px) rotate(-3deg);
  }
  50% {
    transform: translateX(0) rotate(0deg);
  }
  75% {
    transform: translateX(10px) rotate(2deg);
  }
}

// Energy Charge
@keyframes vl-energy-charge-left {
  0% {
    clip-path: polygon(0 100%, 100% 100%, 100% 100%, 0 100%);
    filter: brightness(1);
  }
  40% {
    clip-path: polygon(0 0, 100% 0, 100% 100%, 0 100%);
    filter: brightness(1.3);
  }
  50% {
    clip-path: polygon(0 0, 100% 0, 100% 100%, 0 100%);
    filter: brightness(1.5);
  }
  60%,
  100% {
    clip-path: polygon(0 100%, 100% 100%, 100% 100%, 0 100%);
    filter: brightness(1);
  }
}

@keyframes vl-energy-charge-right {
  0% {
    clip-path: polygon(0 100%, 100% 100%, 100% 100%, 0 100%);
    filter: brightness(1);
  }
  40% {
    clip-path: polygon(0 0, 100% 0, 100% 100%, 0 100%);
    filter: brightness(1.3);
  }
  50% {
    clip-path: polygon(0 0, 100% 0, 100% 100%, 0 100%);
    filter: brightness(1.5);
  }
  60%,
  100% {
    clip-path: polygon(0 100%, 100% 100%, 100% 100%, 0 100%);
    filter: brightness(1);
  }
}

// Typewriter Reveal
@keyframes vl-typewriter-reveal {
  0% {
    clip-path: inset(0 100% 0 0);
  }
  50% {
    clip-path: inset(0 0 0 0);
  }
  50.1% {
    clip-path: inset(0 0 0 0);
  }
  100% {
    clip-path: inset(0 0 0 100%);
  }
}

@keyframes vl-typewriter-cursor {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0;
  }
}

// Spiral Rotation
@keyframes vl-spiral-rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes vl-spiral-scale {
  0%,
  100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(0.7);
    opacity: 0.5;
  }
}
</style>
